import { useEffect, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "react-toastify";
import { WorkflowDataApi } from "../../services/WorkflowDataApi";
import { useTranslation } from "react-i18next";

export const QrScan = ({ startScanning = false, onScan, onError, onStartScanning }) => {
  const { t } = useTranslation();
  const qrCodeRegionId = "html5qr-code-full-region";
  const [html5QrCode, setHtml5QrCode] = useState(null);

  useEffect(() => {
    let qrCodeInstance = null;

    if (startScanning) {
      qrCodeInstance = new Html5Qrcode(qrCodeRegionId);
      setHtml5QrCode(qrCodeInstance);

      const qrCodeSuccessCallback = async (decodedText, decodedResult) => {
        if (decodedText) {
          try {
            // Esperar 2.5 segundos para volver a procesar el QR
            const now = Date.now();
            if (qrCodeInstance.lastErrorTimestamp && (now - qrCodeInstance.lastErrorTimestamp < 2500)) {
              return;
            }

            // Actualizar cooldown inmediatamente para evitar sobrecarga del padre
            qrCodeInstance.lastErrorTimestamp = now;

            // Formato esperado: "equipmentCode|plantCode|code|asset" o "recordId|workflowId|workflow"
            const parts = decodedText.split('|');
            let parsedData = null;

            if (parts.length === 3 && parts[2]?.toLowerCase() === 'workflow') {
              try {
                const api = new WorkflowDataApi();
                const response = await api.detailWorkflowInstance(parseInt(parts[0]));
                const data = response?.data || response;
                
                if (data && data.code) {
                  const codeParts = data.code.split('-');
                  const numericCode = codeParts?.length > 1 ? parseInt(codeParts[1], 10) : parseInt(data.code, 10);
                  
                  parsedData = {
                    type: 'workflow',
                    recordId: numericCode,
                    workflowId: data.workflowId || parseInt(parts[1]),
                    workflow: parts[2]
                  };
                } else {
                  parsedData = {
                    type: 'workflow',
                    recordId: parseInt(parts[0]),
                    workflowId: parseInt(parts[1]),
                    workflow: parts[2]
                  };
                }
              } catch (error) {
                parsedData = {
                  type: 'workflow',
                  recordId: parseInt(parts[0]),
                  workflowId: parseInt(parts[1]),
                  workflow: parts[2]
                };
              }
            } else if (parts.length >= 4) {
              parsedData = {
                type: parts[3]?.toLowerCase() === 'asset' ? 'asset' : 'legacy',
                equipmentCode: parseInt(parts[0]),
                plantId: parseInt(parts[1]),
                sapId: parts[2],
                asset: parts[3]
              };
            } else {
              toast.error(t('qrScanner.errors.invalidFormat'));
              return;
            }

            // Disparar acción de forma asíncrona para no bloquear el hilo
            // de ejecución interno de html5-qrcode y evitar errores de React state
            setTimeout(() => {
              onScan(parsedData);
            }, 10);
            
          } catch (e) {
            qrCodeInstance.lastErrorTimestamp = Date.now();
            toast.error(t('qrScanner.errors.readError'));
          }
        }
      };

      const qrCodeErrorCallback = (errorMessage) => {
        // Ignorar errores en background mientras se escanea
      };

      qrCodeInstance.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        qrCodeSuccessCallback,
        qrCodeErrorCallback
      ).catch(err => {
        if (onError) onError(err);
      });
    }

    // Función de limpieza cuando startScanning se desmonta
    return () => {
      if (qrCodeInstance && qrCodeInstance.isScanning) {
        qrCodeInstance.stop().then(() => {
          qrCodeInstance.clear();
        }).catch(err => {
          console.error("Error stopping scanner", err);
        });
      }
    };
  }, [onScan, onError]);

  const handleClickToStart = () => {
    if (onStartScanning) {
      onStartScanning();
    }
  };

  if (!startScanning) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px",
        minHeight: "200px"
      }}>
        <div 
          style={{
            width: "200px",
            height: "200px",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease"
          }}
          onClick={handleClickToStart}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#1976d2";
            e.currentTarget.style.backgroundColor = "#f5f5f5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#ccc";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div style={{ textAlign: "center", color: "#666" }}>
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>📷</div>
            <div>{t('qrScanner.clickToOpen')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: "100%", 
      maxWidth: "600px", 
      margin: "0 auto",
      borderRadius: "8px",
      overflow: "hidden",
      position: "relative",
      minHeight: "200px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      {/* Mensaje de procesamiento que se muestra detrás del video de la cámara */}
      <div style={{
        position: "absolute",
        width: "200px",
        height: "200px",
        border: "2px dashed #dbdbdb",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        zIndex: 1
      }}>
        <div style={{ textAlign: "center", color: "#dbdbdb", fontWeight: 500 }}>
           {t('qrScanner.processing')}
        </div>
      </div>
      
      {/* Contenedor principal de la cámara. zIndex: 2 lo pone encima del mensaje "Procesando..." una vez que el video carga */}
      <div id={qrCodeRegionId} style={{ width: "100%", zIndex: 2 }}></div>
    </div>
  );
};
